import { signal } from '@preact/signals';
import type { Presence, SocialClientMsg, SocialServerMsg } from '@vm/shared';
import { WS_BASE } from './config';
import { freshAccessToken, refreshSession } from './http';

type MsgType = SocialServerMsg['t'];
type MsgOf<K extends MsgType> = Extract<SocialServerMsg, { t: K }>;
type AnyHandler = (msg: SocialServerMsg) => void;

export type SocialState = 'idle' | 'connecting' | 'open' | 'reconnecting' | 'stopped';

const PING_MS = 25_000;
const MAX_BACKOFF_MS = 30_000;

/**
 * Social WebSocket (chat, presence, party, matchmaking and reward pushes). Reconnects with
 * exponential backoff + jitter, re-sends the last presence on every connect and keeps a
 * short outbox for messages sent while reconnecting.
 */
export class SocialClient {
  readonly state = signal<SocialState>('idle');
  private ws: WebSocket | null = null;
  private handlers = new Map<MsgType, Set<AnyHandler>>();
  private attempt = 0;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private pingTimer: ReturnType<typeof setInterval> | null = null;
  private running = false;
  private presence: { status: Presence; activity?: string } = { status: 'lobby' };
  private outbox: SocialClientMsg[] = [];
  private generation = 0;

  on<K extends MsgType>(type: K, fn: (msg: MsgOf<K>) => void): () => void {
    let set = this.handlers.get(type);
    if (!set) {
      set = new Set();
      this.handlers.set(type, set);
    }
    const h = fn as AnyHandler;
    set.add(h);
    return () => set!.delete(h);
  }

  start(): void {
    if (this.running) return;
    this.running = true;
    this.attempt = 0;
    void this.connect();
  }

  stop(): void {
    this.running = false;
    this.generation++;
    this.clearTimers();
    this.outbox = [];
    if (this.ws) {
      this.ws.onclose = null;
      this.ws.close(1000, 'bye');
      this.ws = null;
    }
    this.state.value = 'stopped';
  }

  /** Forces a reconnect now (e.g. after the network came back). */
  kick(): void {
    if (!this.running || this.state.value === 'open' || this.state.value === 'connecting') return;
    if (this.reconnectTimer) clearTimeout(this.reconnectTimer);
    this.reconnectTimer = null;
    this.attempt = 0;
    void this.connect();
  }

  get connected(): boolean {
    return this.ws?.readyState === WebSocket.OPEN;
  }

  send(msg: SocialClientMsg): boolean {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(msg));
      return true;
    }
    if (msg.t === 'chat' && this.running) {
      this.outbox = [...this.outbox.slice(-9), msg];
      return false;
    }
    return false;
  }

  setPresence(status: Presence, activity?: string): void {
    this.presence = { status, activity };
    this.send({ t: 'presence', status, activity });
  }

  private emit(msg: SocialServerMsg): void {
    const set = this.handlers.get(msg.t);
    if (!set) return;
    for (const h of set) {
      try {
        h(msg);
      } catch (e) {
        console.error('social handler failed', e);
      }
    }
  }

  private clearTimers(): void {
    if (this.reconnectTimer) clearTimeout(this.reconnectTimer);
    if (this.pingTimer) clearInterval(this.pingTimer);
    this.reconnectTimer = null;
    this.pingTimer = null;
  }

  private async connect(): Promise<void> {
    if (!this.running) return;
    const gen = ++this.generation;
    this.state.value = this.attempt === 0 ? 'connecting' : 'reconnecting';
    let token: string | null = null;
    try {
      token = await freshAccessToken();
    } catch {
      token = null;
    }
    if (gen !== this.generation || !this.running) return;
    if (!token) {
      this.scheduleReconnect();
      return;
    }
    let ws: WebSocket;
    try {
      ws = new WebSocket(`${WS_BASE}/v1/ws?token=${encodeURIComponent(token)}`);
    } catch {
      this.scheduleReconnect();
      return;
    }
    this.ws = ws;
    let opened = false;
    let kicked = false;
    ws.onopen = () => {
      if (gen !== this.generation) return;
      opened = true;
      this.attempt = 0;
      this.state.value = 'open';
      ws.send(JSON.stringify({ t: 'presence', status: this.presence.status, activity: this.presence.activity } satisfies SocialClientMsg));
      const pending = this.outbox;
      this.outbox = [];
      for (const m of pending) ws.send(JSON.stringify(m));
      if (this.pingTimer) clearInterval(this.pingTimer);
      this.pingTimer = setInterval(() => this.send({ t: 'ping' }), PING_MS);
    };
    ws.onmessage = (e) => {
      if (gen !== this.generation || typeof e.data !== 'string') return;
      let msg: SocialServerMsg;
      try {
        msg = JSON.parse(e.data) as SocialServerMsg;
      } catch {
        return;
      }
      if (!msg || typeof msg.t !== 'string') return;
      if (msg.t === 'kicked') kicked = true;
      this.emit(msg);
    };
    ws.onerror = () => {};
    ws.onclose = (e) => {
      if (gen !== this.generation) return;
      if (this.pingTimer) clearInterval(this.pingTimer);
      this.pingTimer = null;
      this.ws = null;
      if (!this.running) return;
      if (kicked) {
        this.running = false;
        this.state.value = 'stopped';
        return;
      }
      // 4001/4401/1008: token rejected — refresh before retrying.
      if (e.code === 4001 || e.code === 4401 || e.code === 1008 || (!opened && this.attempt >= 1)) {
        void refreshSession().catch(() => false);
      }
      this.scheduleReconnect();
    };
  }

  private scheduleReconnect(): void {
    if (!this.running) return;
    this.state.value = 'reconnecting';
    const base = Math.min(MAX_BACKOFF_MS, 1000 * 2 ** Math.min(this.attempt, 5));
    const delay = Math.round(base * (0.7 + Math.random() * 0.6));
    this.attempt++;
    if (this.reconnectTimer) clearTimeout(this.reconnectTimer);
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      void this.connect();
    }, delay);
  }
}

export const social = new SocialClient();
